/**
 * Storybook-only helpers for campaign stories (PrizePop + Tournaments).
 *
 * The stories exist to produce STATIC, deterministic snapshots of the real
 * components (visual-regression baselines) — no live data, no ticking clocks,
 * no network. Two rules make that work:
 *
 * 1. SEED SYNCHRONOUSLY, BEFORE FIRST PAINT. Campaign components resolve
 *    `campaignConfigRegistry[campaignType]` and the selected campaign during
 *    their FIRST render — a `useEffect` seed (the old story pattern) runs
 *    after that render and crashes the story. `seedCampaignStore` is called
 *    from the story wrapper's render body instead.
 *
 * 2. FIXED DATES ONLY, ALWAYS >3 DAYS AWAY (or in the past). The countdown
 *    (`createCountdownResult`) ticks HH:MM:SS only inside a 3-day window;
 *    beyond it it renders a static calendar date, and past dates clamp
 *    stable. Fixed far-future/past dates therefore make every render
 *    pixel-identical regardless of when the snapshot is taken. Never use
 *    `Date.now()`-relative dates here (that's the QA panel's live-session
 *    pattern — right for QA, wrong for snapshots).
 *
 * 3. EVERY META WHOSE STORIES CALL THIS MUST SET
 *    `docs: { story: { inline: false } }`. The autodocs page mounts ALL of a
 *    component's stories in ONE React tree; two render-body seeders with
 *    different data ping-pong the shared campaignsStore into an infinite
 *    rerender loop and freeze the browser tab. (The `lastSeedKey` guard below
 *    only converges for a single seeder — it cannot, even in principle, when
 *    concurrent stories legitimately want different store states.) Per-story
 *    doc iframes give each story its own store instance.
 *
 * Fetch suppression is a side effect of NOT seeding session/host settings:
 * every campaign query gates `enabled` on a campaign `host` plus
 * `sessionKey`/`playerId` (see `useCampaignsQuery` / `useTournamentLeaderboard`),
 * none of which stories seed, so all queries stay disabled and no network is
 * touched. Leaderboard rows are injected directly into the query cache via
 * `queryClient.setQueryData` — a disabled query still serves that cache.
 * Storybook stays independent of `campaignsStore.mockMode` (a QA-panel-only
 * concept); coupling the two let the QA leaderboard branch override injected
 * story data.
 */

import { useAppStore } from 'store/app/appStore';
import { useCampaignsStore } from '../store/campaignsStore';
import type { CampaignSource, CampaignTypes } from '../types/campaign.types';
import type { CampaignPanelInfo } from '../components/MultiViewCampaignSection/types';

/* Fixed story dates — chosen so nothing on screen ever ticks. */
export const STORY_FAR_FUTURE_START = '2030-06-10T10:00:00Z';
export const STORY_FAR_FUTURE_END = '2030-06-30T20:00:00Z';
export const STORY_PAST_START = '2024-01-10T10:00:00Z';
export const STORY_PAST_END = '2024-01-20T20:00:00Z';
/** Running "now": started long ago, ends far in the future (no ticking). */
export const STORY_ACTIVE_START = STORY_PAST_START;
export const STORY_ACTIVE_END = STORY_FAR_FUTURE_END;

// fallow-ignore-next-line unused-export
export const STORY_BALANCE = {
  units: 100,
  currency: 'EUR',
  balance: 50000,
  formattedBalance: '500.00',
};

export interface SeedCampaignStoreArgs {
  campaigns: CampaignSource[];
  /** The globally active campaign type (`campaignsStore.campaignType`). */
  type: CampaignTypes;
  collapsed?: boolean;
  /**
   * Override the store's computed `hasMany[type]` flag. When omitted it is
   * DERIVED the same way the app's `setCampaigns` action derives it — more
   * than one campaign of `type` in the list — so stories match production
   * semantics by default (raw `setState` seeding would otherwise leave the
   * flag at its default `false` and the card CSS/asset resolution keyed off
   * `data-has-many` would take the wrong branch).
   */
  hasMany?: boolean;
  /** Per-type panel info (`campaignsStore.panelInfos[type]`). */
  panelInfo?: CampaignPanelInfo;
  /** Extra `useAppStore` state merged over the story defaults. */
  app?: Record<string, unknown>;
}

let lastSeedKey = '';

/**
 * Seed the campaign + app stores for one story render. Called from the story
 * wrapper's RENDER BODY (not an effect) so the state exists before the
 * component under test first reads it. Idempotent per seed-key, so React
 * strict-mode double renders and Storybook arg tweaks re-seed only when the
 * inputs actually changed.
 *
 * Selection uses the per-type `selectedCampaignIds` map (the shape that
 * replaced `selectedPrizePopId` in the tournaments migration): the first
 * campaign of each type in the list becomes that type's selection.
 */
export function seedCampaignStore({ campaigns, type, collapsed = false, hasMany, panelInfo, app }: SeedCampaignStoreArgs): void {
  const key = JSON.stringify([campaigns, type, collapsed, hasMany, panelInfo, app]);
  if (key === lastSeedKey) return;
  lastSeedKey = key;

  const selectedCampaignIds: Record<string, string | null> = {};
  for (const c of campaigns) {
    if (c.type && selectedCampaignIds[c.type] == null) selectedCampaignIds[c.type] = c.id;
  }

  useCampaignsStore.setState({
    campaigns,
    campaignType: type,
    selectedCampaignIds,
    isHeaderCollapsed: {
      ...useCampaignsStore.getState().isHeaderCollapsed,
      [type]: collapsed,
    },
    hasMany: {
      ...useCampaignsStore.getState().hasMany,
      [type]: hasMany !== undefined ? hasMany : campaigns.filter((c) => c.type === type).length > 1,
    },
    ...(panelInfo
      ? { panelInfos: { ...useCampaignsStore.getState().panelInfos, [type]: panelInfo } }
      : {}),
  });

  useAppStore.setState({
    viewMode: 'single',
    balance: STORY_BALANCE,
    ...(app || {}),
  });
}
